import { call, put } from "redux-saga/effects";
import financialService from "@/services/financialService";
import { financialConstants } from "@/actions/constants/financialConstants";
import { financialActions } from "@/actions/financialActions";
import { eventActions } from "@/actions/eventActions";
import { showSuccessToast, showErrorToast } from "@/utils/toast";

const errMsg = (e: any, fallback: string): string => e?.response?.data?.errorMessage || e?.response?.data?.message || e?.message || fallback;

export function* getFinancial(action: any): Generator<any, void, any> {
  const eventId: string = action.payload;
  try {
    yield put(eventActions.setLoadingFlag(eventId, "financial", true));
    const response = yield call(financialService.getEventFinancial, eventId);
    if (response?.success) {
      yield put({ type: financialConstants.getFinancialSuccess });
      yield put(eventActions.setFinancialData(eventId, response.resultObject));
      yield put(eventActions.setLoadedFlag(eventId, "financial", true));
      yield put(eventActions.setLoadingFlag(eventId, "financial", false));
    } else {
      const msg = response?.errorMessage || "Failed to fetch financial data";
      yield put({ type: financialConstants.getFinancialFailure, payload: msg });
      yield put(eventActions.setLoadingFlag(eventId, "financial", false));
      showErrorToast(msg);
    }
  } catch (error: any) {
    const msg = errMsg(error, "Failed to fetch financial data");
    yield put({ type: financialConstants.getFinancialFailure, payload: msg });
    yield put(eventActions.setLoadingFlag(eventId, "financial", false));
    showErrorToast(msg);
  }
}

export function* createPayment(action: any): Generator<any, void, any> {
  const { formData, eventId, onSuccess, onError } = action.payload;
  try {
    const response = yield call(financialService.createPayment, formData);
    if (response?.success) {
      showSuccessToast(response.message || "Payment added successfully");
      if (onSuccess) onSuccess();
      yield put(financialActions.getFinancialRequest(eventId));
    } else {
      const msg = response?.errorMessage || "Failed to create payment";
      showErrorToast(msg);
      if (onError) onError();
    }
  } catch (error: any) {
    showErrorToast(errMsg(error, "Failed to create payment"));
    if (onError) onError();
  }
}

export function* updatePayment(action: any): Generator<any, void, any> {
  const { paymentId, formData, eventId, onSuccess, onError } = action.payload;
  try {
    const response = yield call(financialService.updatePayment, paymentId, formData);
    if (response?.success) {
      showSuccessToast(response.message || "Payment updated successfully");
      if (onSuccess) onSuccess();
      yield put(financialActions.getFinancialRequest(eventId));
    } else {
      const msg = response?.errorMessage || "Failed to update payment";
      showErrorToast(msg);
      if (onError) onError();
    }
  } catch (error: any) {
    showErrorToast(errMsg(error, "Failed to update payment"));
    if (onError) onError();
  }
}

export function* deletePayment(action: any): Generator<any, void, any> {
  const { paymentId, eventId } = action.payload;
  try {
    const response = yield call(financialService.deletePayment, paymentId);
    if (response?.success) {
      showSuccessToast(response.message || "Payment deleted");
      yield put(financialActions.getFinancialRequest(eventId));
    } else {
      showErrorToast(response?.errorMessage || "Failed to delete payment");
    }
  } catch (error: any) {
    showErrorToast(errMsg(error, "Failed to delete payment"));
  }
}

export function* createFinancialAlert(action: any): Generator<any, void, any> {
  const { data, eventId, onSuccess, onError } = action.payload;
  try {
    const response = yield call(financialService.createFinancialAlert, data);
    if (response?.success) {
      showSuccessToast(response.message || "Alert created successfully");
      if (onSuccess) onSuccess();
      yield put(financialActions.getFinancialRequest(eventId));
    } else {
      const msg = response?.errorMessage || "Failed to create alert";
      showErrorToast(msg);
      if (onError) onError();
    }
  } catch (error: any) {
    showErrorToast(errMsg(error, "Failed to create alert"));
    if (onError) onError();
  }
}

export function* updateFinancialAlert(action: any): Generator<any, void, any> {
  const { alertId, data, eventId, onSuccess, onError } = action.payload;
  try {
    const response = yield call(financialService.updateFinancialAlert, alertId, data);
    if (response?.success) {
      showSuccessToast(response.message || "Alert updated successfully");
      if (onSuccess) onSuccess();
      yield put(financialActions.getFinancialRequest(eventId));
    } else {
      const msg = response?.errorMessage || "Failed to update alert";
      showErrorToast(msg);
      if (onError) onError();
    }
  } catch (error: any) {
    showErrorToast(errMsg(error, "Failed to update alert"));
    if (onError) onError();
  }
}

export function* deleteFinancialAlert(action: any): Generator<any, void, any> {
  const { alertId, eventId } = action.payload;
  try {
    const response = yield call(financialService.deleteFinancialAlert, alertId);
    if (response?.success) {
      showSuccessToast(response.message || "Alert deleted");
      yield put(financialActions.getFinancialRequest(eventId));
    } else {
      showErrorToast(response?.errorMessage || "Failed to delete alert");
    }
  } catch (error: any) {
    showErrorToast(errMsg(error, "Failed to delete alert"));
  }
}

export function* createExpenseContact(action: any): Generator<any, void, any> {
  const { data, eventId, onSuccess, onError } = action.payload;
  try {
    const response = yield call(financialService.createExpenseContact, data);
    if (response?.success) {
      showSuccessToast(response.message || "Expense contact created successfully");
      if (onSuccess) onSuccess();
      yield put(financialActions.getFinancialRequest(eventId));
    } else {
      const msg = response?.errorMessage || "Failed to create expense contact";
      showErrorToast(msg);
      if (onError) onError();
    }
  } catch (error: any) {
    showErrorToast(errMsg(error, "Failed to create expense contact"));
    if (onError) onError();
  }
}

export function* createExpense(action: any): Generator<any, void, any> {
  const { data, eventId, onSuccess, onError } = action.payload;
  try {
    const response = yield call(financialService.createExpense, data);
    if (response?.success) {
      showSuccessToast(response.message || "Expense created successfully");
      if (onSuccess) onSuccess();
      yield put(financialActions.getFinancialRequest(eventId));
    } else {
      const msg = response?.errorMessage || "Failed to create expense";
      showErrorToast(msg);
      if (onError) onError();
    }
  } catch (error: any) {
    showErrorToast(errMsg(error, "Failed to create expense"));
    if (onError) onError();
  }
}

export function* updateExpense(action: any): Generator<any, void, any> {
  const { expenseId, data, eventId, onSuccess, onError } = action.payload;
  try {
    const response = yield call(financialService.updateExpense, expenseId, data);
    if (response?.success) {
      showSuccessToast(response.message || "Expense updated successfully");
      if (onSuccess) onSuccess();
      yield put(financialActions.getFinancialRequest(eventId));
    } else {
      const msg = response?.errorMessage || "Failed to update expense";
      showErrorToast(msg);
      if (onError) onError();
    }
  } catch (error: any) {
    showErrorToast(errMsg(error, "Failed to update expense"));
    if (onError) onError();
  }
}

export function* deleteExpense(action: any): Generator<any, void, any> {
  const { expenseId, eventId } = action.payload;
  try {
    const response = yield call(financialService.deleteExpense, expenseId);
    if (response?.success) {
      showSuccessToast(response.message || "Expense deleted");
      yield put(financialActions.getFinancialRequest(eventId));
    } else {
      showErrorToast(response?.errorMessage || "Failed to delete expense");
    }
  } catch (error: any) {
    showErrorToast(errMsg(error, "Failed to delete expense"));
  }
}

export function* createPayout(action: any): Generator<any, void, any> {
  const { data, eventId, onSuccess, onError } = action.payload;
  try {
    const response = yield call(financialService.createPayout, data);
    if (response?.success) {
      showSuccessToast(response.message || "Payout created successfully");
      if (onSuccess) onSuccess();
      yield put(financialActions.getFinancialRequest(eventId));
    } else {
      const msg = response?.errorMessage || "Failed to create payout";
      showErrorToast(msg);
      if (onError) onError();
    }
  } catch (error: any) {
    showErrorToast(errMsg(error, "Failed to create payout"));
    if (onError) onError();
  }
}

export function* updatePayout(action: any): Generator<any, void, any> {
  const { payoutId, data, eventId, onSuccess, onError } = action.payload;
  try {
    const response = yield call(financialService.updatePayout, payoutId, data);
    if (response?.success) {
      showSuccessToast(response.message || "Payout updated successfully");
      if (onSuccess) onSuccess();
      yield put(financialActions.getFinancialRequest(eventId));
    } else {
      const msg = response?.errorMessage || "Failed to update payout";
      showErrorToast(msg);
      if (onError) onError();
    }
  } catch (error: any) {
    showErrorToast(errMsg(error, "Failed to update payout"));
    if (onError) onError();
  }
}

export function* deletePayout(action: any): Generator<any, void, any> {
  const { payoutId, eventId } = action.payload;
  try {
    const response = yield call(financialService.deletePayout, payoutId);
    if (response?.success) {
      showSuccessToast(response.message || "Payout deleted");
      yield put(financialActions.getFinancialRequest(eventId));
    } else {
      showErrorToast(response?.errorMessage || "Failed to delete payout");
    }
  } catch (error: any) {
    showErrorToast(errMsg(error, "Failed to delete payout"));
  }
}

export function* updateExpenseContact(action: any): Generator<any, void, any> {
  const { contactId, data, eventId, onSuccess, onError } = action.payload;
  try {
    const response = yield call(financialService.updateExpenseContact, contactId, data);
    if (response?.success) {
      showSuccessToast(response.message || "Expense contact updated successfully");
      if (onSuccess) onSuccess();
      yield put(financialActions.getFinancialRequest(eventId));
    } else {
      const msg = response?.errorMessage || "Failed to update expense contact";
      showErrorToast(msg);
      if (onError) onError();
    }
  } catch (error: any) {
    showErrorToast(errMsg(error, "Failed to update expense contact"));
    if (onError) onError();
  }
}

export function* deleteExpenseContact(action: any): Generator<any, void, any> {
  const { contactId, eventId } = action.payload;
  try {
    const response = yield call(financialService.deleteExpenseContact, contactId);
    if (response?.success) {
      showSuccessToast(response.message || "Expense contact deleted");
      yield put(financialActions.getFinancialRequest(eventId));
    } else {
      showErrorToast(response?.errorMessage || "Failed to delete expense contact");
    }
  } catch (error: any) {
    showErrorToast(errMsg(error, "Failed to delete expense contact"));
  }
}
